import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import './RyanAdminSettings.css';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
  fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts?.headers || {}) } });

type TargetKey = 'sales' | 'commissions' | 'representatives' | 'users' | 'accessRequests';

interface TargetDef { key: TargetKey; label: string; description: string; }

const TARGETS: TargetDef[] = [
  { key: 'sales', label: 'Histórico de vendas', description: 'Apaga todos os registros de vendas (abas Vendas e Histórico).' },
  { key: 'commissions', label: 'Comissões e pagamentos de embaixadores', description: 'Apaga o ledger de comissões e o histórico de pagamentos formais.' },
  { key: 'representatives', label: 'Embaixadores', description: 'Apaga os cadastros de embaixadores. Não apaga as comissões deles, a menos que essa opção também esteja marcada.' },
  { key: 'users', label: 'Contas de usuário do RYAN', description: 'Apaga apenas os acessos da plataforma RYAN — nunca toca em contas do GRAIA ENEM, e nunca apaga a sua própria conta de admin.' },
  { key: 'accessRequests', label: 'Solicitações de acesso', description: 'Apaga o histórico de solicitações enviadas pela página de vendas.' },
];

const emptySelection = (): Record<TargetKey, boolean> => ({
  sales: false, commissions: false, representatives: false, users: false, accessRequests: false,
});

const RyanAdminSettings: React.FC = () => {
  const [selected, setSelected] = useState<Record<TargetKey, boolean>>(emptySelection());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewCounts, setPreviewCounts] = useState<Record<string, number> | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [purging, setPurging] = useState(false);
  const [result, setResult] = useState<{ counts: Record<string, number> } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const selectedKeys = TARGETS.filter(t => selected[t.key]).map(t => t.key);
  const allSelected = selectedKeys.length === TARGETS.length;
  const labelFor = (key: string) => TARGETS.find(t => t.key === key)?.label.toLowerCase() || key;

  const toggleTarget = (key: TargetKey) => setSelected(s => ({ ...s, [key]: !s[key] }));

  const toggleAll = () => {
    const next = !allSelected;
    setSelected(Object.fromEntries(TARGETS.map(t => [t.key, next])) as Record<TargetKey, boolean>);
  };

  const openConfirm = async () => {
    if (selectedKeys.length === 0) return;
    setResult(null);
    setErrorMsg(null);
    setConfirmText('');
    setConfirmOpen(true);
    setPreviewLoading(true);
    try {
      const r = await api(`/admin/settings/purge-preview?targets=${selectedKeys.join(',')}`);
      if (r.ok) {
        const d = await r.json();
        setPreviewCounts(d.counts);
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeConfirm = () => { setConfirmOpen(false); setPreviewCounts(null); setConfirmText(''); };

  const handlePurge = async () => {
    if (confirmText.trim().toUpperCase() !== 'APAGAR') return;
    setPurging(true);
    setErrorMsg(null);
    try {
      const r = await api('/admin/settings/purge', { method: 'POST', body: JSON.stringify({ targets: selectedKeys }) });
      const d = await r.json();
      if (!r.ok) { setErrorMsg(d.message || 'Erro ao apagar.'); return; }
      setResult({ counts: d.counts });
      setSelected(emptySelection());
      closeConfirm();
    } catch {
      setErrorMsg('Erro de conexão.');
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="ras-pane">
      <div className="rst-danger-zone">
        <div className="rst-danger-header">
          <AlertTriangle size={22} />
          <div>
            <div className="rst-danger-title">Zona de risco</div>
            <div className="rst-danger-subtitle">As ações abaixo apagam dados permanentemente. Não há como desfazer.</div>
          </div>
        </div>

        <label className="rst-select-all">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} />
          Selecionar tudo
        </label>

        <div className="rst-target-list">
          {TARGETS.map(t => (
            <label key={t.key} className="rst-target-row">
              <input type="checkbox" checked={!!selected[t.key]} onChange={() => toggleTarget(t.key)} />
              <div>
                <div className="rst-target-label">{t.label}</div>
                <div className="rst-target-desc">{t.description}</div>
              </div>
            </label>
          ))}
        </div>

        {result && (
          <div className="ras-msg ras-msg--ok">
            Apagado com sucesso: {Object.entries(result.counts).map(([k, v]) => `${v} ${labelFor(k)}`).join(', ')}.
          </div>
        )}

        <button className="rst-purge-btn" onClick={openConfirm} disabled={selectedKeys.length === 0}>
          <Trash2 size={14} /> Apagar selecionado{selectedKeys.length !== 1 ? 's' : ''}
        </button>
      </div>

      {confirmOpen && ReactDOM.createPortal(
        <div className="ras-modal-overlay" onClick={closeConfirm}>
          <div className="ras-modal" onClick={e => e.stopPropagation()}>
            <div className="ras-modal-header">
              <div>
                <div className="ras-modal-title">Confirmar exclusão</div>
                <div className="ras-modal-subtitle">Esta ação não pode ser desfeita.</div>
              </div>
              <button className="ras-modal-close" onClick={closeConfirm}><X size={18} /></button>
            </div>
            <div className="ras-modal-body">
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Isso vai apagar:</div>
                {previewLoading && <div className="ras-modal-empty">Calculando…</div>}
                {!previewLoading && previewCounts && (
                  <ul className="rst-preview-list">
                    {selectedKeys.map(k => (
                      <li key={k}><strong>{previewCounts[k] ?? 0}</strong> {labelFor(k)}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Para confirmar, digite <strong>APAGAR</strong> abaixo</div>
                <input className="ras-input" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="APAGAR" />
              </div>
              {errorMsg && <div className="ras-msg ras-msg--err">{errorMsg}</div>}
            </div>
            <div className="ras-modal-footer">
              <button className="ras-btn-modal-close" onClick={closeConfirm}>Cancelar</button>
              <button
                className="rst-purge-confirm-btn"
                onClick={handlePurge}
                disabled={purging || previewLoading || confirmText.trim().toUpperCase() !== 'APAGAR'}
              >
                {purging ? 'Apagando…' : 'Apagar definitivamente'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RyanAdminSettings;
